import * as History from 'history';
import { Component } from 'react';
import { CurrencyContext, IWithContextProps, core, withContext } from '@tock/coretypes';
import { DoubleBooking } from 'components/business/checkout/DoubleBookingWarning';
import { FlowRouteComponentProps } from 'components/render-prop-components/FlowRoute';
import { Head } from 'head/Head';
import { RequestableState } from '@tock/shared/state/requestableState';
import { RouteComponentProps } from 'react-router-dom';
import { Typography } from 'omakase';
import { css } from '@emotion/react';
import { getAmountDue } from '@tock/shared/utils/purchase';
import { getDoubleBookingStatus } from 'selectors/purchaseSelectors';
import { getGiftCardBalance } from 'selectors/patronSelectors';
import { ownerAbleToSetupAccountSelector } from 'selectors/appSelector';
import { shouldUsePaidQuickCheckout } from 'utils/quickCheckoutUtils';
import { stripePublicKeySelector } from 'selectors/settingsSelector';
import CompletePurchase from 'components/business/checkout/CompletePurchase';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import RescheduleHeader from 'components/account/reschedule/RescheduleHeader';
import ReschedulePurchaseDetails from 'components/business/checkout/ReschedulePurchaseDetails';
import Signin, { SignInMode } from 'components/sign-in/Signin';
import _ from 'lodash';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface OwnProps extends RouteComponentProps, FlowRouteComponentProps {
  app: StoreType['app'];
  business: StoreType['business'];
  isAuthenticated: boolean;
  isSubmitting: boolean;
  location: History.Location;
  lock: StoreType['lock'];
  onLockExpiration(): void;
  onReschedule(purchaseOrder?: core.IPurchaseOrder): Promise<unknown>;
  onSuccess(purchaseOrder: core.IPurchaseOrder): void;
  purchase: StoreType['purchase'];
  reschedule: StoreType['reschedule'];
  showOptions: boolean;
  selection: StoreType['selection'];
}

export interface ConnectProps extends DefaultConnectProps {
  calendar: StoreType['calendar'];
  delivery: StoreType['delivery'];
  giftCard: StoreType['giftCard'];
  lastGuest?: StoreType['lastGuest'];
  lock: StoreType['lock'];
  loyaltyProgram: StoreType['loyaltyProgram'];
  ownerAbleToSetupAccount: boolean;
  paymentCard: StoreType['paymentCard'];
  purchase: StoreType['purchase'];
  selection: StoreType['selection'];
  giftCardBalance: RequestableState<core.TockGiftCardBalanceResponse>;
  ui: StoreType['ui'];
  doubleBooking: DoubleBooking;
  stripePublicKey: string;
}

type RescheduleConfirmRouteProps = OwnProps & ConnectProps & IWithContextProps;

const title = 'Confirm changes';

interface State {
  tab: SignInMode;
}

class RescheduleConfirmRoute extends Component<RescheduleConfirmRouteProps, State> {
  state: State = {
    tab: 'login',
  };

  componentDidMount() {
    this.props.actions.navigation.incrementalUpdate({ title });
    if (this.props.ownerAbleToSetupAccount) {
      this.setState(() => ({
        tab: 'signup',
      }));
    }
  }

  componentDidUpdate(prevProps: RescheduleConfirmRouteProps) {
    if (!prevProps.ownerAbleToSetupAccount && this.props.ownerAbleToSetupAccount) {
      this.setState(() => ({
        tab: 'signup',
      }));
    }
  }

  handleClickBack = () => {
    const { history } = this.props;
    history.goBack();
  };

  handleLockExpiration = () => {
    this.props.onLockExpiration();
  };

  subtitle({
    requireLoginAndProfile,
    ownerAbleToSetupAccount,
  }: {
    requireLoginAndProfile?: boolean;
    ownerAbleToSetupAccount?: boolean;
  }) {
    const { business, purchase } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const businessName = business.business!.name;
    const amountDue = purchase.calculatedPrice ? getAmountDue(purchase.calculatedPrice) : 0;
    let message = `Please confirm your modified reservation at
      ${businessName}. ${
        amountDue > 0 ? 'Changes to this reservation require additional charges.' : ''
      } If you continue, the original
      reservation will be canceled${amountDue > 0 ? '' : ' and you will not be charged any fees'}.`;

    if (amountDue > 0) {
      if (ownerAbleToSetupAccount) {
        message =
          'The updated selection you made will require payment. Please finish setting up your Tock account so we can securely collect your payment information and complete your reservation.';
      } else if (requireLoginAndProfile) {
        message =
          'The updated selection you made will require payment. Please log in to your Tock account so we can securely collect your payment information and complete your reservation.';
      }
    }

    return <Typography variant="body02">{message}</Typography>;
  }

  handleChangeTab = (tab: SignInMode) => {
    this.setState(() => ({ tab }));
  };

  render() {
    const {
      app,
      business,
      isAuthenticated,
      location,
      lock,
      onReschedule,
      onSuccess,
      ownerAbleToSetupAccount,
      reschedule,
      selection,
      showOptions,
      stripePublicKey,
      context: { isFeatureEnabled },
    } = this.props;
    const { jwtToken } = app;
    if (!business.business || !reschedule.purchase || !lock.currentLock) {
      // Only needed on a page refresh.
      return <LoadingComponent isLoading={true} isBallPulse={true} />;
    }

    if (!lock.currentLock) {
      return null;
    }

    const selectedExperience = _.first(lock.currentLock.ticketType) as core.TicketType;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const businessName = business.business.name!;
    const currentLockIsPaid = (lock.currentLock?.type?.[0].amount ?? 0) > 0;
    // We enable PQC in the case of a purchase going from free to paid (e.g large party deposits)
    const newPurchaseIsPQCAndPaid =
      isFeatureEnabled('PAID_QUICK_CHECKOUT') &&
      isFeatureEnabled('PAID_QUICK_CHECKOUT_EDITS') &&
      !isAuthenticated &&
      currentLockIsPaid;

    /**
     * Chooses between CompletePurchase and ReschedulePurchaseDetails flows
     * CompletePurchase => all paid reservations, and logged-in free reservations.
     *   - if PAID_QUICK_CHECKOUT is disabled and it's a paid reservation being edited, the user will be prompted to login first.
     * ReschedulePurchaseDetails => must both be logged out and be editing a free reservation.
     */
    const usesPaidQuickCheckout =
      shouldUsePaidQuickCheckout(
        reschedule.purchase,
        jwtToken,
        isFeatureEnabled('PAID_QUICK_CHECKOUT')
      ) || newPurchaseIsPQCAndPaid;

    const shouldUseCompletePurchase =
      usesPaidQuickCheckout || !!(isAuthenticated && reschedule.purchase.canEdit && app.patron);

    const lockedSeatingAreaName = lock.currentLock.ticketSubset?.[0].seatingArea?.name;

    const requireLoginAndProfile =
      !usesPaidQuickCheckout &&
      reschedule.purchase.canEdit &&
      !isAuthenticated &&
      !app.patron &&
      (currentLockIsPaid || !!reschedule.purchase.paymentCardHold); //figure out if we need the user to be logged in because of the amount

    return (
      <div className="Reschedule-content PageContent-inner-bottom-only">
        <CurrencyContext.Provider value={business.business?.currencyCode ?? 'en-US'}>
          <Head title={`${businessName} - Reschedule`} />
          <RescheduleHeader
            location={location}
            lockedUntilMs={lock.lockedUntilMs ?? undefined}
            onClickBack={this.handleClickBack}
            onLockExpiration={this.handleLockExpiration}
            title={title}
            subtitle={this.subtitle({
              requireLoginAndProfile,
              ownerAbleToSetupAccount,
            })}
            showOptions={showOptions}
          />

          <div
            css={
              requireLoginAndProfile
                ? css`
                    padding: 0;
                  `
                : undefined
            }
            className="Reschedule-body--calendar is-profile"
          >
            {shouldUseCompletePurchase || requireLoginAndProfile ? (
              <>
                {shouldUseCompletePurchase ? (
                  <CompletePurchase
                    {...this.props}
                    stripePublicKey={stripePublicKey}
                    isOwnerSameAsDiner={true}
                    basePurchase={reschedule.purchase}
                    business={business.business}
                    selectedExperience={selectedExperience}
                    onSuccess={onSuccess}
                    shouldAskForSmsConsent={false}
                  />
                ) : null}
                {requireLoginAndProfile ? (
                  <Signin
                    postSigninPath={`/${reschedule?.purchase?.business?.domainName}/reschedule/confirm`}
                    tab={this.state.tab}
                    hideHeader={true}
                    onChangeTab={this.handleChangeTab}
                    initialGuest={(reschedule.purchase.ownerPatron as core.Patron) ?? undefined}
                    isAccountActivation={ownerAbleToSetupAccount}
                    isLoginOnly={requireLoginAndProfile && !ownerAbleToSetupAccount}
                    submitText="Continue"
                  />
                ) : null}
              </>
            ) : (
              <ReschedulePurchaseDetails
                app={app}
                business={business.business}
                selection={selection}
                isAuthenticated={isAuthenticated}
                purchase={reschedule.purchase}
                selectedExperience={selectedExperience}
                onReschedule={onReschedule}
                onSuccess={onSuccess}
                isSubmitting={reschedule.requestInProgress}
                seatingAreaName={lockedSeatingAreaName || undefined}
              />
            )}
          </div>
        </CurrencyContext.Provider>
      </div>
    );
  }
}

function mapStateToProps(state: StoreType) {
  const {
    calendar,
    delivery,
    giftCard,
    lastGuest,
    lock,
    loyaltyProgram,
    paymentCard,
    purchase,
    selection,
    ui,
  } = state;
  return {
    calendar,
    delivery,
    doubleBooking: getDoubleBookingStatus(state),
    giftCard,
    giftCardBalance: getGiftCardBalance(state),
    lastGuest,
    lock,
    loyaltyProgram,
    paymentCard,
    purchase,
    selection,
    stripePublicKey: stripePublicKeySelector(state),
    ui,
    ownerAbleToSetupAccount: ownerAbleToSetupAccountSelector(state),
  };
}

export default connect<OwnProps, ConnectProps>(
  withContext(RescheduleConfirmRoute),
  mapStateToProps
);
